// ===========================================================================
// The room's shared video.
//
// Everyone watches the same moment of the same thing. The server holds the
// position and broadcasts it; each viewer streams from the provider
// themselves, so Lumi never fetches or relays a frame of somebody else's work.
//
// This file knows nothing about any particular site. Where a video may come
// from lives in the shared registry (video-providers.js); how to drive a given
// player lives in an adapter (video-adapters.js). Adding a site means touching
// those, not this.
//
// NOTHING is requested from the provider until the viewer says so. There is no
// iframe, no thumbnail, no preconnect, until they press the button -- so
// somebody who declines has made no third-party request at all. The consent
// gate is not a nicety here; it is the privacy boundary.
//
// ASCII only.
// ===========================================================================

import { t } from '../i18n.js';
import { el } from '../dom.js';
import {
  VideoAction,
  videoEmbedUrl,
  videoWatchUrl,
  videoProvider,
  videoFrameSandbox,
  videoCanFollow,
} from '@lumi/shared';
import { createVideoAdapter } from './video-adapters.js';

// How far out of step before we drag the player back. Under a second or two,
// correcting is more jarring than the drift.
const DRIFT_MS = 2000;
// How far a RELOAD-synchronised player may be out before it is worth the
// interruption of rebuilding its frame. Much larger than DRIFT_MS: a seek is
// cheap and a reload is not.
const RELOAD_DRIFT_MS = 4000;
// Never rebuild more often than this, whatever arrives.
const RELOAD_COOLDOWN_MS = 2500;
// A jump bigger than this between two readings is somebody dragging the
// player's own scrubber, not playback advancing.
const SEEK_JUMP_MS = 5000;
// How long to disregard the player's reports after we move it ourselves.
const QUIET_MS = 1800;

/**
 * @param {{ onControl: (action: string, atMs?: number) => void,
 *           onPin: (url: string) => void,
 *           onShare: (on: boolean) => void,
 *           reportControl?: () => HTMLElement }} h
 *   reportControl, when given, builds a control for reporting what is playing
 *   (a teen room; rooms.js decides). It is shown while something is pinned.
 */
export function createRoomVideo(h) {
  let state = { provider: null, videoId: null };
  let isOwner = false;
  let allowed = false; // has THIS viewer agreed to load from the provider
  let frame = null;
  let adapter = null;
  let playerError = null;

  // What the player says it is doing -- not what we asked it to do. Without
  // this the only options are seeking on every broadcast, which stutters, or
  // never correcting, which lets people fall behind and stay there.
  let playerAtMs = null;
  let playerPlaying = null;
  let pendingSync = false;
  let silenceTimer = null; // watches for a player that never says anything
  let durationMs = 0; // as the player reports it, so the host gets a timeline
  // After WE move the player, it reports the move back. Without a quiet
  // window we would read our own command as the host pressing something and
  // send it to the server again.
  let quietUntil = 0;
  let lastPosMs = null;
  // Whether this frame has had its first sync. The initial one is special:
  // the position is already in the embed URL, so it needs no seek.
  let settledOnce = false;
  // What the current frame was built to show, for providers we can only
  // synchronise by rebuilding it.
  let frameAtMs = 0;
  let frameBuiltAt = 0;
  let framePlaying = false;
  let lastReloadAt = 0;
  let pendingReload = null;
  let retryTimer = null;
  let retriedOnce = false; // one automatic retry per video, never a loop
  // Whether this player has shown it can be SYNCHRONISED -- which means it
  // has told us a position, a duration, or whether it is playing.
  //
  // Answering at all is not enough, and assuming it was is what shipped a
  // "Play for everyone" button that did nothing. OK.ru greets us with
  // {"event":"inited"} and from then on ignores every command and never
  // reports anything. It talks; it does not obey.
  //
  // A position is the right proof because it is the thing sync is MADE of:
  // a player that will not tell you where it is cannot be kept in step with
  // anybody, whatever else it says.
  let syncProven = false;

  /**
   * Said under the video when a player we expected to talk has not.
   *
   * It used to announce that the player "was blocked from using storage", which
   * was true of one case I had a console for and false of the next one I met: VK
   * plays perfectly and simply does not answer our handshake. From outside a
   * cross-origin frame, a player that loaded and ignored us is indistinguishable
   * from one that never started -- so this must not claim to know which.
   *
   * It now leads with what IS known and true either way (the room follows by
   * reloading, or it cannot follow at all), and offers tracking protection as a
   * possibility rather than a diagnosis.
   */
  function showBlocked() {
    // Built as a list rather than passed inline: replaceChildren stringifies a
    // null child (it is not el(), which skips one), so a provider with no watch
    // URL used to end the sentence with the word "null". The dangling "-- " has
    // to go with it, or the sentence stops mid-clause.
    const watch = videoWatchUrl(state);
    const children = [
      el('span', { text: `${statusLine()} ` }),
      el('span', {
        class: 'muted',
        text: watch
          ? "If it never starts at all, your browser's tracking protection may be stopping it. "
          : "If it never starts at all, your browser's tracking protection may be stopping it.",
      }),
    ];
    if (watch) {
      children.push(
        el('a', { href: watch, target: '_blank', rel: 'noreferrer noopener', text: `watch it on ${providerName()}` }),
        el('span', { text: '.' }),
      );
    }
    status.replaceChildren(...children);
  }

  function proveSync() {
    clearTimeout(silenceTimer); // it spoke; whatever else is true, it is alive
    if (syncProven) return;
    syncProven = true;
    refreshBars();
    refreshTimeline();
    status.textContent = statusLine();
  }
  let lastPosAt = 0;
  let ended = false;
  let lastFollowed = null; // the last id we re-pinned, so we do it once

  // The watch-together queue (D7), as the server last broadcast it, and who
  // this screen is -- the entries that are mine and the turn that is mine
  // are the only two things about the queue that differ per viewer.
  let queue = { queue: [], dj: null, skipVotes: 0, needed: 0 };
  let selfId = null;
  let nextSentFor = null; // the video we already asked to move on from

  const stage = el('div', { class: 'rv-stage' });
  const status = el('div', { class: 'rv-status muted' });
  const queueBox = el('div', { class: 'rv-queue hidden' });

  // ---- the timeline -------------------------------------------------------
  //
  // The host runs it for the room. Skipping an ad, or getting past somebody
  // taking four minutes to reach the point, is most of what anybody wants from
  // a shared video, and there was no way to do either: the only seek in the UI
  // was 'back to start'.
  //
  // Built ONCE and kept, rather than rebuilt with the button bars -- a control
  // that is replaced mid-drag drops the drag.
  const scrub = el('input', {
    type: 'range',
    class: 'rv-scrub',
    min: '0',
    max: '10000',
    value: '0',
    'aria-label': 'Position in the video',
  });
  const timeLabel = el('span', { class: 'rv-time', text: '' });
  const timeline = el('div', { class: 'rv-timeline hidden' }, [
    el('button', {
      class: 'btn btn-ghost btn-sm rv-nudge',
      text: '-10s',
      title: t('Back ten seconds, for everyone'),
      onClick: () => nudge(-10_000),
    }),
    scrub,
    el('button', {
      class: 'btn btn-ghost btn-sm rv-nudge',
      text: t('+30s'),
      title: t('Skip forward thirty seconds, for everyone'),
      onClick: () => nudge(30_000),
    }),
    timeLabel,
  ]);
  const root = el('div', { class: 'room-video hidden' }, [stage, timeline, status, queueBox]);

  let dragging = false;
  const scrubMs = () => (durationMs ? (Number(scrub.value) / 10_000) * durationMs : 0);

  scrub.addEventListener('input', () => {
    dragging = true; // stop the tick fighting the thumb under their finger
    timeLabel.textContent = `${clock(scrubMs())} / ${clock(durationMs)}`;
  });
  scrub.addEventListener('change', () => {
    const to = scrubMs();
    dragging = false;
    h.onControl(VideoAction.SEEK, Math.round(to));
  });

  function nudge(byMs) {
    const to = Math.max(0, Math.min(durationMs || Number.MAX_SAFE_INTEGER, expectedMs() + byMs));
    h.onControl(VideoAction.SEEK, Math.round(to));
  }

  function clock(ms) {
    const secs = Math.max(0, Math.floor(ms / 1000));
    const mm = Math.floor(secs / 60);
    const ss = String(secs % 60).padStart(2, '0');
    const hh = Math.floor(mm / 60);
    return hh ? `${hh}:${String(mm % 60).padStart(2, '0')}:${ss}` : `${mm}:${ss}`;
  }

  /**
   * Everyone sees where the room is; only the host may move it.
   *
   * Read-only for everybody else on purpose: knowing how far in you are, and
   * how much is left, is worth having even when you cannot change it.
   */
  function refreshTimeline() {
    // The scrubber needs a duration, which only a talking player gives us.
    const drivable = !!state.videoId && syncProven && durationMs > 0 && allowed;
    timeline.classList.toggle('hidden', !drivable);
    const mayMove = drivable && isOwner;
    scrub.disabled = !mayMove;
    timeline.classList.toggle('rv-readonly', !mayMove);
    for (const b of timeline.querySelectorAll('.rv-nudge')) b.disabled = !mayMove;
    if (dragging || !drivable) return;
    const at = Math.min(expectedMs(), durationMs);
    scrub.value = String(Math.round((at / durationMs) * 10_000));
    timeLabel.textContent = `${clock(at)} / ${clock(durationMs)}`;
  }

  const def = () => videoProvider(state.provider);
  /**
   * How much of the queue is shown before it is folded.
   *
   * Four rows is enough to answer "what is after this one", which is what a
   * queue is for most of the time. The whole thing is one tap away, and the
   * count is always on the header, so nothing is hidden -- it is just not all
   * in the way of the chat at once.
   */
  // The queue is a SCROLL WINDOW, not a fold. It used to show four rows and
  // a "Show all N" button; the rest of the list was simply absent, which read
  // as "my video did not go in" to whoever had just added one. Now every row
  // is there, in a box a few rows tall that scrolls, with the header (count,
  // whose turn, the vote, Play next) fixed above it. The box is sized in
  // styles.css against the viewport too, so on a phone the panel never runs
  // past the stage that clips it. How many rows were queued when we last
  // drew, so an addition of our own can be scrolled into view.
  let drawnQueueLength = 0;

  const providerName = () => def()?.name || 'the site';

  /**
   * Does this link carry a playlist?
   *
   * Any YouTube link with a `list` in it, not just a bare /playlist page --
   * because YouTube's own share button, used while a playlist is playing,
   * hands you `watch?v=...&list=...`, and that is how most people share one.
   * Only Add to queue asks this. Pin still plays the single video, because
   * "play this now" means this. Mirrors queueablePlaylistId on the server,
   * which is the copy that decides.
   */
  const hasPlaylist = (raw) => {
    try {
      const u = new URL(String(raw || '').trim());
      const host = u.hostname.replace(/^www\.|^m\./, '').toLowerCase();
      const known = host === 'youtube.com' || host === 'music.youtube.com' || host === 'youtu.be';
      return known && !!u.searchParams.get('list');
    } catch {
      return false;
    }
  };
  // Audio providers exist, and calling a track a video reads as a mistake to
  // somebody looking straight at a waveform.
  const isAudio = () => def()?.kind === 'audio';
  const verb = () => (isAudio() ? 'listen to together' : 'watch together');

  // Visible when there is something to watch, OR when you are the owner and
  // could pin something. Both, because a room with no video correctly sends
  // none, and an owner's panel would otherwise stay hidden forever with the
  // pin box unreachable.
  function refreshVisibility() {
    // ...or when the queue has something in it: whoever added it should see
    // it there, and the host's turn to play it needs somewhere to be pressed.
    root.classList.toggle('hidden', !state.videoId && !isOwner && !queue.queue.length);
    // Visible is not the same as PLAYING, and the stylesheet needs to know
    // the difference. Seventeen layout rules read "the panel is not hidden"
    // as "a video is on", and the panel is shown to every owner from the
    // moment they open an empty room so the pin box has somewhere to live.
    // The whole room was therefore laid out for a video nobody had pinned:
    // the camera grid crushed to a 5.5rem filmstrip, the transcript floor
    // dropped to 2.5rem, the stage frozen at flex: none, and on a short
    // landscape window a text room flipped into the two-column video layout.
    root.classList.toggle('rv-idle', !state.videoId);
  }

  // Reporting what is playing, when the room offers it. Built once and moved
  // into each new set of bars, so a broadcast redrawing them does not close
  // its menu.
  const reportEl = typeof h.reportControl === 'function' ? h.reportControl() : null;

  function refreshBars() {
    // The pin row is everyone's once there is something to watch: the owner
    // pins and queues, everyone else queues. The server refuses what it must.
    const built = el(
      'div',
      { class: 'rv-bars' },
      [
        ownerBar(),
        viewerBar(),
        isOwner || state.videoId || queue.queue.length ? pinBar() : null,
        state.videoId ? reportEl : null,
      ].filter(Boolean),
    );
    const existing = root.querySelector('.rv-bars');
    if (existing) existing.replaceWith(built);
    else root.append(built);
  }

  // ---- where the room should be right now ---------------------------------
  //
  // The broadcast is a position AT A MOMENT, so while playing it has to be
  // wound forward. Comparing the player against a figure frozen at the last
  // broadcast would report growing drift that is not there, and seek forever.
  let stateAt = 0;
  function expectedMs() {
    const base = state.atMs || 0;
    return state.playing ? base + (Date.now() - stateAt) : base;
  }

  /**
   * Bring the player into line with the room.
   *
   * Seeks ONLY against a real reading. The old rule seeked whenever the
   * position was unknown -- precisely the state a freshly loaded player is in
   * -- so every video got a seek the instant it appeared, replacing the image
   * that had just been drawn with black. Initial position needs no seek: it is
   * already in the embed URL.
   */
  function quiet() {
    quietUntil = Date.now() + QUIET_MS;
  }

  function sync(force = false) {
    if (!allowed || !adapter || !state.videoId || !adapter.attemptsSync) return;
    const known = playerAtMs !== null;
    const drift = known ? Math.abs(playerAtMs - expectedMs()) : null;
    // A DELIBERATE move: the server broadcast a new position after this frame
    // had already settled, so somebody pressed something.
    const deliberate = force && settledOnce;

    // Seek when we can see we are out of step, OR when the room says it moved
    // and we cannot see where the player is.
    //
    // That second case was missing, and it is why skipping did nothing for
    // some people. Position only becomes known once a player reports one, and
    // a player that has not started reports nothing -- so on a phone or tablet
    // where autoplay is blocked, playerAtMs stays null forever and every seek
    // was silently skipped. Play still worked, because play never needed to
    // know the position. Hence "only play for everyone seems to work".
    //
    // Refusing to seek blind is still right for the FIRST sync -- seeking a
    // freshly loaded player replaces the frame it just drew with black, and
    // the initial position is already in the embed URL. It is only wrong
    // afterwards.
    if ((known && (force || drift > DRIFT_MS)) || (!known && deliberate)) {
      quiet();
      adapter.send('seek', expectedMs());
    }
    // And only move it when it is not already doing the right thing. An
    // unstarted player is already paused; saying so again is what blanks it.
    if (state.playing && playerPlaying !== true) {
      quiet();
      adapter.send('play');
      // There was an overlay here offering to "tap to join what everyone is
      // watching". It is gone, and should not come back.
      //
      // It was meant for browsers that refuse to start a player without a
      // gesture. But we can only tell that has happened if the player reports
      // its state, and the players that need the prompt are largely the ones
      // that report nothing -- so it appeared over videos that were already
      // playing, and its tap sent commands those same players ignore. Twice I
      // narrowed the condition instead of accepting that the signal it needed
      // does not exist.
      //
      // Somebody whose browser blocked playback already has a play button in
      // front of them: the player's own. It works, which is more than this
      // did.
    } else if (!state.playing && (playerPlaying === true || (deliberate && playerPlaying === null))) {
      // Same gap: a player that never reported leaves playerPlaying null, and
      // requiring a strict true meant pause never reached it either. Pausing
      // something already paused costs nothing.
      quiet();
      adapter.send('pause');
    }
    settledOnce = true;
  }

  /** May this viewer start and stop the video for everybody? */
  function mayAct() {
    return isOwner || !!state.sharedControl;
  }

  /**
   * The player's own play button, made to mean what people expect.
   *
   * There are two play buttons on screen: the big one in the middle of the
   * video, and ours. Everybody presses the big one -- and on its own it starts
   * the video in that tab only, so the host sits watching alone while the room
   * stares at a still frame, with nothing on screen explaining why. Rather
   * than try to make our button louder than YouTube's, we let theirs be the
   * room's: when somebody who is allowed to act uses it, we tell the server.
   */
  function mirrorPlayState(on) {
    if (!allowed || !state.videoId || !syncProven) return;
    if (on === !!state.playing) return; // already agrees; nothing to say
    if (Date.now() < quietUntil) return; // this is our own command echoing back
    if (!mayAct()) {
      // Not theirs to change -- put them back with everyone else instead.
      sync(true);
      return;
    }
    quiet();
    h.onControl(on ? VideoAction.PLAY : VideoAction.PAUSE);
  }

  /**
   * The host dragging the player's own scrubber counts as moving the room.
   *
   * Detected as a jump: between two reports a second apart, playback advances
   * by about a second. Anything much larger was a person, not the clock.
   */
  function mirrorSeek(ms) {
    const now = Date.now();
    const prev = lastPosMs;
    const prevAt = lastPosAt;
    lastPosMs = ms;
    lastPosAt = now;
    if (prev === null || !isOwner || !allowed || !state.videoId) return;
    if (Date.now() < quietUntil) return;
    const expectedAdvance = state.playing ? now - prevAt : 0;
    if (Math.abs(ms - (prev + expectedAdvance)) <= SEEK_JUMP_MS) return;
    quiet();
    h.onControl(VideoAction.SEEK, Math.round(ms));
  }


  /** Where the current frame believes it is, if nothing has touched it. */
  function framePositionMs() {
    return frameAtMs + (framePlaying ? Date.now() - frameBuiltAt : 0);
  }

  /**
   * Synchronise a player that will not take commands, by rebuilding its frame
   * at the room's position.
   *
   * Several of these players answer nothing we send them -- measured, one at a
   * time, against real videos. But their embed URLs all accept a start
   * position, and a frame can be rebuilt. So the room can still put everybody
   * at the same moment; it just costs a reload rather than a seek.
   *
   * Only ever called when the server says something changed. Never on a timer:
   * if a browser blocks the reloaded frame from playing, a drift-driven rebuild
   * would reload forever.
   */
  function reloadSync() {
    if (syncProven || !allowed || !state.videoId || !frame) return;
    if (!videoCanFollow(state.provider)) return;
    const drift = Math.abs(framePositionMs() - expectedMs());
    if (framePlaying === !!state.playing && drift < RELOAD_DRIFT_MS) return;

    // COALESCE, never drop. The cooldown used to return early, which threw
    // the change away -- so a host pressing +30s twice quickly left everyone
    // else a minute behind, permanently, because nothing else would ever
    // trigger a rebuild. Now the newest state simply wins: a later change
    // replaces the pending one and the room ends up where the host is.
    const wait = Math.max(0, RELOAD_COOLDOWN_MS - (Date.now() - lastReloadAt));
    clearTimeout(pendingReload);
    pendingReload = setTimeout(swapFrame, wait);
  }

  /**
   * Rebuild the frame at the room's position WITHOUT a black gap.
   *
   * The new frame is built underneath, and the old one is only removed once
   * the new one has loaded. Replacing them the other way round shows everyone
   * a hole where the video was, several seconds long on a slow connection,
   * every single time the host touches anything.
   */
  function swapFrame() {
    pendingReload = null;
    if (!allowed || !state.videoId || !frame) return;
    lastReloadAt = Date.now();
    const old = frame;
    const next = buildFrame({ autoplay: !!state.playing });
    if (!next) return;
    next.classList.add('rv-swapping');
    stage.append(next);
    frame = next;
    let settled = false;
    const reveal = () => {
      if (settled) return;
      settled = true;
      next.classList.remove('rv-swapping');
      old.remove();
    };
    next.addEventListener('load', reveal, { once: true });
    // A frame that never fires load must not leave the old one on screen for
    // ever, showing a position nobody is at any more.
    setTimeout(reveal, 6000);
  }

  function buildFrame({ autoplay = false } = {}) {
    const at = expectedMs();
    const src = videoEmbedUrl({
      provider: state.provider,
      videoId: state.videoId,
      atMs: at,
      origin: location.origin,
      autoplay,
    });
    frameAtMs = at;
    frameBuiltAt = Date.now();
    framePlaying = !!state.playing;
    if (!src) return null; // unknown provider, or an id that no longer validates

    const f = el('iframe', {
      class: 'rv-frame',
      src,
      title: `Shared ${isAudio() ? 'audio' : 'video'} from ${providerName()}`,
      // storage-access lets an embedded player ask for its own storage in a
      // browser that partitions third-party state by default. Several of these
      // players stall on their way up without it, showing a spinner forever and
      // no error anybody can act on.
      allow: 'autoplay; encrypted-media; picture-in-picture; fullscreen; storage-access',
      // Declared by the provider, defaulted by the registry -- see
      // DEFAULT_FRAME_SANDBOX for why allow-same-origin belongs there.
      sandbox: videoFrameSandbox(state.provider),
      // NOT no-referrer. Players check the Referer against the origin they were
      // handed and refuse to start without one -- that was YouTube's "Error
      // 153". This sends the origin and never the path, so the room id and
      // invite code still do not reach them.
      referrerpolicy: 'strict-origin-when-cross-origin',
      frameborder: '0',
    });

    adapter?.destroy();
    playerAtMs = null;
    playerPlaying = null;

    // A player that we KNOW talks, that then says nothing at all, is not a
    // protocol we got wrong -- it is a player that never really started. In
    // practice that is the viewer's browser: tracking protection or a private
    // window blocking the third-party storage and analytics these players load
    // on their way up. Nothing here can override that, but a black rectangle
    // with no explanation is the worst possible way to say so.
    // One automatic retry for a player that never speaks. A stalled embed is
    // often just a bad first load -- a partial cache, a request that lost a
    // race -- and rebuilding it costs a second where giving up costs the whole
    // feature. Once only, and only for players we know normally talk, so this
    // can never become a reload loop.
    clearTimeout(retryTimer);
    if (def()?.control && !retriedOnce) {
      retryTimer = setTimeout(() => {
        if (syncProven || !frame || !allowed) return;
        retriedOnce = true;
        adapter?.destroy();
        adapter = null;
        frame = null;
        render();
      }, 8000);
    }

    clearTimeout(silenceTimer);
    if (def()?.control) {
      // Comfortably longer than the handshake keeps retrying (twelve tries over
      // nine seconds). Measured against a real SoundCloud widget, the reply that
      // proves sync arrived five to eight seconds in -- so a twelve-second
      // warning left about three seconds of margin and would cry wolf on any
      // slower connection than the one I tested on.
      silenceTimer = setTimeout(() => {
        if (!syncProven && frame && allowed) showBlocked();
      }, 25_000);
    }

    adapter = createVideoAdapter(def()?.control || null, f, {
      onReady: () => {
        // Deliberately does NOT prove sync -- see syncProven. A greeting
        // only means somebody is home.
        if (pendingSync) {
          pendingSync = false;
          sync(true);
        }
      },
      onPosition: (ms) => {
        // A position advancing in step with the clock IS playing, whatever the
        // player says about its state -- and some do not say anything we can
        // map. Niconico reports its position happily while never reporting a
        // status we recognise, so the room believed nothing was playing and
        // kept offering to start something that was already running.
        //
        // Checked BEFORE mirrorSeek, which is where the previous reading is
        // kept. Only playing is ever inferred: silence is not evidence of a
        // pause, but movement is evidence of play.
        if (lastPosMs !== null && lastPosAt) {
          const advanced = ms - lastPosMs;
          const elapsed = Date.now() - lastPosAt;
          // Roughly in step, not a jump -- a seek also moves the position.
          if (elapsed > 250 && advanced > 0 && Math.abs(advanced - elapsed) < Math.max(1000, elapsed * 0.6)) {
            playerPlaying = true;
          }
        }
        playerAtMs = ms;
        proveSync();
        mirrorSeek(ms);
      },
      onDuration: (ms) => {
        durationMs = ms;
        proveSync();
        refreshTimeline();
      },
      onPlaying: (on) => {
        playerPlaying = on;
        proveSync();
        if (on) {
          ended = false;
        }
        mirrorPlayState(on);
      },
      onEnded: () => {
        ended = true;
        status.textContent = statusLine();
        // The server never learns a video ended -- the player is in this
        // browser -- so whoever holds the remote says "next" for the room:
        // the owner, or the member whose turn it is. Once per video, and only
        // when there is something queued to move on to.
        const mayAdvance = isOwner || (selfId && queue.dj === selfId);
        if (mayAdvance && queue.queue.length && nextSentFor !== state.videoId) {
          nextSentFor = state.videoId;
          h.onQueue?.('next');
        }
      },
      onVideoId: (id) => {
        // The player moved itself -- almost always the host clicking one of the
        // suggestions at the end. Nobody else follows that click, so the room
        // quietly splits: the host is watching something the others cannot see
        // and cannot be told about. Re-pin it and bring everyone along.
        if (!isOwner || !id || id === state.videoId || id === lastFollowed) return;
        lastFollowed = id;
        h.onPin(`${state.provider}:${id}`);
      },
      onError: (code, message) => showError(message, code),
    });
    return f;
  }

  /**
   * Say what went wrong, and offer the way out.
   *
   * Replaces the frame rather than sitting under it: a dead black rectangle
   * with an explanation beneath reads as two problems instead of one.
   */
  function showError(message, code) {
    playerError = message;
    adapter?.destroy();
    adapter = null;
    frame = null;
    const watch = videoWatchUrl(state);
    stage.replaceChildren(
      el('div', { class: 'rv-gate' }, [
        el('p', { class: 'rv-gate-title', text: t('This will not play here') }),
        el('p', { class: 'rv-gate-body', text: message }),
        el('div', { class: 'rv-gate-actions' }, [
          watch
            ? el('a', {
                class: 'btn btn-sm',
                href: watch,
                target: '_blank',
                rel: 'noreferrer noopener',
                text: `Watch on ${providerName()}`,
              })
            : null,
          isOwner
            ? el('button', {
                class: 'btn btn-ghost btn-sm',
                text: t('Unpin it'),
                onClick: () => h.onControl(VideoAction.STOP),
              })
            : null,
        ].filter(Boolean)),
      ]),
    );
    status.textContent = Number.isFinite(code) && code > 0 ? `Player error ${code}` : '';
  }

  // ---- the consent gate ---------------------------------------------------
  function gate() {
    const who = providerName();
    const watch = videoWatchUrl(state);
    return el('div', { class: 'rv-gate' }, [
      el('p', { class: 'rv-gate-title', text: `Someone pinned something from ${who} to ${verb()}` }),
      el('p', {
        class: 'rv-gate-body',
        text:
          `Playing it loads it from ${who}, which means they will see that request. `
          + 'Nothing has been sent to them yet, and nothing will if you leave this alone.',
      }),
      el('div', { class: 'rv-gate-actions' }, [
        el('button', {
          class: 'btn btn-primary btn-sm',
          text: t('Load and watch'),
          onClick: () => {
            allowed = true;
            render();
            // Agreeing is also what earns you the play/pause buttons, so the
            // bars are rebuilt now rather than on the next broadcast.
            refreshBars();
          },
        }),
        watch
          ? el('a', {
              class: 'btn btn-ghost btn-sm',
              href: watch,
              target: '_blank',
              rel: 'noreferrer noopener',
              text: `Open on ${who} instead`,
            })
          : null,
      ].filter(Boolean)),
    ]);
  }

  // ---- owner controls -----------------------------------------------------
  const pinInput = el('input', {
    type: 'text',
    class: 'rv-pin-input',
    placeholder: t('Paste a link'),
    maxlength: '500',
  });

  function ownerBar() {
    if (!isOwner) return null;
    const canDrive = syncProven || videoCanFollow(state.provider);
    const bits = [
      canDrive && state.videoId
        ? el('button', {
            class: `btn btn-sm ${state.sharedControl ? '' : 'btn-ghost'}`,
            text: state.sharedControl ? 'Everyone can play/pause' : 'Only you control it',
            title: t('Whether everyone may play and pause. Seeking and stopping stay yours either way.'),
            onClick: () => h.onShare(!state.sharedControl),
          })
        : null,
      state.videoId
        ? el('button', {
            class: 'btn btn-ghost btn-sm',
            text: t('Stop'),
            title: t('End the shared video for everyone'),
            onClick: () => h.onControl(VideoAction.STOP),
          })
        : null,
    ].filter(Boolean);
    return bits.length ? el('div', { class: 'rv-owner' }, bits) : null;
  }

  function viewerBar() {
    // Shown when you may act: the owner always, everyone else only while
    // control is shared. The server enforces this regardless.
    // Available for reload-followed providers as well: play, pause and the
    // skips all work through a rebuild, even though a scrubber cannot.
    const followable = syncProven || videoCanFollow(state.provider);
    if (!state.videoId || !allowed || !followable) return null;
    if (!isOwner && !state.sharedControl) return null;
    return el('div', { class: 'rv-controls' }, [
      el('button', {
        // "for everyone" because the video has its own play button inches away
        // that does not do that, and nothing else on screen distinguishes them.
        class: `btn btn-sm ${state.playing ? '' : 'btn-primary'}`,
        text: state.playing ? 'Pause for everyone' : 'Play for everyone',
        onClick: () => h.onControl(state.playing ? VideoAction.PAUSE : VideoAction.PLAY),
      }),
      isOwner
        ? el('button', {
            class: 'btn btn-ghost btn-sm',
            text: t('Back to start'),
            onClick: () => h.onControl(VideoAction.SEEK, 0),
          })
        : null,
    ].filter(Boolean));
  }

  function pinBar() {
    return el('div', { class: 'rv-pin' }, [
      pinInput,
      isOwner
        ? el('button', {
            class: 'btn btn-sm',
            text: state.videoId ? 'Replace' : 'Pin it',
            onClick: () => {
              const v = pinInput.value.trim();
              if (!v) return;
              h.onPin(v);
              pinInput.value = '';
            },
          })
        : null,
      // Anyone may queue. Same input, so the paste-and-press gesture is the
      // same whether you are pinning now or lining something up for later.
      el('button', {
        class: `btn btn-sm ${isOwner ? 'btn-ghost' : ''}`,
        text: t('Add to queue'),
        title: t('Line this up to play after what is on now'),
        onClick: () => {
          const v = pinInput.value.trim();
          if (!v) return;
          // A playlist link queues the playlist. Anything else queues one
          // video, exactly as before.
          h.onQueue?.(hasPlaylist(v) ? 'add_playlist' : 'add', { url: v });
          pinInput.value = '';
        },
      }),
    ].filter(Boolean));
  }

  // ---- the queue (D7) ----------------------------------------------------------
  //
  // What is lined up, whose turn it is, and the running vote to skip. Drawn
  // in full from the last broadcast, like everything else here.

  const djName = () => {
    const e = queue.queue.find((x) => x.by === queue.dj);
    return (e && e.byHandle) || 'Someone';
  };

  // Folded away, and remembered.
  //
  // On a tablet the panel stacks video, then ten queue rows, then the paste
  // row, then the roster, then the rules card, and the message list is what
  // gives: it was squeezed to a sliver with the composer still on screen, so
  // the room chat in a watch-together room had nowhere to put a message. The
  // queue is the biggest block and the one you do not need open while you are
  // talking, so it is the one that folds.
  const QUEUE_KEY = 'lumi.queue.folded';
  function readFolded() {
    try {
      const v = localStorage.getItem(QUEUE_KEY);
      // No stored choice: folded on a short viewport, open on a roomy one.
      // The complaint only exists where the panel does not fit.
      if (v === null) return window.innerHeight < 900;
      return v === '1';
    } catch {
      return false;
    }
  }
  function writeFolded(on) {
    try {
      localStorage.setItem(QUEUE_KEY, on ? '1' : '0');
    } catch {
      /* not remembered, but applied for this session */
    }
  }
  let queueFolded = readFolded();

  function renderQueue() {
    const q = queue;
    const has = q.queue.length > 0;
    const canVote = !!state.videoId; // something is on to be voted off
    queueBox.classList.toggle('hidden', !has && !canVote);
    if (!has && !canVote) return;
    const mine = (e) => !!selfId && e.by === selfId;
    const myTurn = !!selfId && q.dj === selfId;
    const shown = q.queue;
    const head = el('div', { class: 'rv-queue-head' }, [
      el('span', { class: 'rv-queue-title-text', text: has ? `Up next (${q.queue.length})` : 'Nothing queued yet' }),
      q.dj
        ? el('span', { class: 'muted', text: myTurn ? 'Your turn to play the next one' : `DJ: ${djName()}` })
        : null,
      // Only with something queued to skip to. With an empty queue the button
      // read "Vote to skip (0 of 0 needed)" and there was nothing for a skip
      // to play (the operator, 2026-09-15).
      canVote && has
        ? el('button', {
            class: 'btn btn-ghost btn-sm',
            text: `Vote to skip (${q.skipVotes} of ${q.needed} needed)`,
            title: t('More than half of the people here can skip what is on'),
            onClick: () => h.onQueue?.('skip_vote'),
          })
        : null,
      has && (isOwner || myTurn)
        ? el('button', {
            class: 'btn btn-sm',
            text: state.videoId ? 'Play next' : 'Play the queue',
            onClick: () => h.onQueue?.('next'),
          })
        : null,
      // Same word the rules card uses, so there is one idiom for folding
      // things away in this room rather than two.
      has
        ? el('button', {
            class: 'btn btn-ghost btn-sm rv-queue-fold',
            text: queueFolded ? 'Show' : 'Hide',
            title: t('Fold this away, or open it again'),
            aria: { expanded: String(!queueFolded) },
            onClick: (ev) => {
              queueFolded = !queueFolded;
              writeFolded(queueFolded);
              ev.currentTarget.textContent = queueFolded ? 'Show' : 'Hide';
              ev.currentTarget.setAttribute('aria-expanded', String(!queueFolded));
              queueBox.classList.toggle('is-folded', queueFolded);
            },
          })
        : null,
    ].filter(Boolean));
    const rows = shown.map((e, i) => {
      const watch = videoWatchUrl(e);
      return el('div', { class: `rv-queue-row${e.by === q.dj ? ' is-dj' : ''}` }, [
        el('span', { class: 'rv-queue-n', text: String(i + 1) }),
        // The name of the video, once the provider has told us what it is.
        // Eight rows of "YouTube: 2DBkOJd_bv8" say nothing about what is
        // coming; the id is the fallback, not the label. The title arrives a
        // moment after the row does, so a row never waits on a lookup.
        (() => {
          const label = e.title || `${videoProvider(e.provider)?.name || e.provider}: ${e.videoId}`;
          const tip = e.title ? [e.title, e.byline].filter(Boolean).join(', ') : label;
          const attrs = { class: `rv-queue-title${e.title ? '' : ' is-id'}`, text: label, title: tip };
          return watch
            ? el('a', { ...attrs, href: watch, target: '_blank', rel: 'noreferrer noopener' })
            : el('span', attrs);
        })(),
        el('span', { class: 'rv-queue-by', text: `${e.byHandle || 'Someone'}${e.by === q.dj ? ' (DJ)' : ''}${mine(e) ? ' (you)' : ''}` }),
        isOwner || mine(e)
          ? el('button', {
              class: 'btn btn-ghost btn-sm',
              text: t('Remove'),
              onClick: () => h.onQueue?.('remove', { index: i }),
            })
          : null,
      ].filter(Boolean));
    });
    // The rows live in their own scroll box under a fixed header. A row you
    // just added is scrolled into view, so adding to a long queue shows you
    // it went in; anybody else's additions leave your scroll position alone.
    const list = el('div', { class: 'rv-queue-list' }, rows);
    const grew = q.queue.length > drawnQueueLength;
    const newestIsMine = q.queue.length > 0 && mine(q.queue[q.queue.length - 1]);
    const previous = queueBox.querySelector('.rv-queue-list');
    const keepScroll = previous ? previous.scrollTop : 0;
    queueBox.classList.toggle('is-folded', queueFolded && has);
    queueBox.replaceChildren(head, list);
    list.scrollTop = grew && newestIsMine ? list.scrollHeight : keepScroll;
    drawnQueueLength = q.queue.length;
  }

  function render() {
    stage.replaceChildren();
    if (!state.videoId) {
      adapter?.destroy();
      adapter = null;
      frame = null;
      allowed = false; // a new video is a new decision
      playerError = null;
      status.textContent = '';
      return;
    }
    if (playerError) return; // already explained; do not rebuild a dead frame
    if (!allowed) {
      adapter?.destroy();
      adapter = null;
      frame = null;
      timeline.classList.add('hidden');
      stage.append(gate());
      return;
    }
    if (!frame) {
      pendingSync = true;
      // Built to autoplay when the room is already playing, as swapFrame's
      // frames are: somebody arriving mid-video, or a queued video coming up,
      // was otherwise left with a still player to tap. The consent click in
      // front of it is the gesture a browser asks for.
      frame = buildFrame({ autoplay: !!state.playing });
      if (!frame) {
        showError('This link is no longer one Lumi can play.', 0);
        return;
      }
    }
    stage.append(frame);
  }

  // Periodic re-sync. A player that stalls -- buffering, a backgrounded tab, a
  // phone that slept -- would otherwise stay behind until somebody happened to
  // press something. Only acted on when the drift is real.
  setInterval(() => {
    if (!allowed || !state.videoId || !state.playing) return;
    sync(false);
  }, 10_000);

  // The position moves with the clock, so the timeline has to as well.
  setInterval(refreshTimeline, 500);

  function statusLine() {
    if (!state.videoId) return '';
    if (ended) {
      return isOwner
        ? 'Finished. Pin something else, or pick one of the suggestions: the room will follow you.'
        : 'Finished.';
    }
    if (!syncProven) {
      // Two different situations, and they deserve different sentences.
      if (videoCanFollow(state.provider)) {
        return `${providerName()} will not take commands, so the room follows the host by `
          + 'reloading at their position. Expect a brief pause when they move it.';
      }
      return `${providerName()} plays for each person separately: the room cannot share a position.`;
    }
    return state.playing ? '' : 'Paused';
  }

  return {
    root,

    /** Owner-only affordances; called when ownership changes hands. */
    setOwner(on) {
      isOwner = !!on;
      refreshVisibility();
      refreshBars();
      refreshTimeline(); // the timeline becomes theirs to move, or stops being
      renderQueue(); // Remove and Play next are the owner's too
    },

    /**
     * The queue, as broadcast (D7), and who this screen is. Handed together
     * because the two things about a queue that differ per viewer -- which
     * entries are mine, whether the turn is mine -- both need selfId.
     */
    setQueue(next, self) {
      queue = next && Array.isArray(next.queue)
        ? { queue: next.queue, dj: next.dj || null, skipVotes: Number(next.skipVotes) || 0, needed: Number(next.needed) || 0 }
        : { queue: [], dj: null, skipVotes: 0, needed: 0 };
      if (self) selfId = self;
      refreshVisibility();
      refreshBars();
      renderQueue();
    },

    /** The whole state, as broadcast. */
    setState(next) {
      const incoming = next || { provider: null, videoId: null };
      const changed = incoming.videoId !== state.videoId || incoming.provider !== state.provider;
      // Consent is per PROVIDER, not per video. The boundary it protects is
      // whether a third party gets a request from this browser at all -- and
      // once you have agreed to load from a site, the next video from the same
      // site discloses nothing further. Re-asking on every change would also
      // mean a gate in everyone's face each time the host skips to the next
      // thing, which teaches people to click through it without reading.
      const newProvider = incoming.provider !== state.provider;
      state = incoming;
      stateAt = Date.now(); // the clock this position was true at
      refreshVisibility();
      if (changed) {
        adapter?.destroy();
        adapter = null;
        frame = null;
        if (newProvider) allowed = false; // a different site is a new decision
        playerError = null; // a failure is never inherited
        playerAtMs = null;
        playerPlaying = null;
        durationMs = 0;
        ended = false;
        lastFollowed = null;
        lastPosMs = null;
        retriedOnce = false; // a new video earns a fresh attempt
        clearTimeout(retryTimer);
        clearTimeout(pendingReload);
        pendingReload = null;
        settledOnce = false; // a new frame gets the same grace as the first
        syncProven = false; // a new player has to prove itself again
        clearTimeout(silenceTimer);
        render();
      } else if (syncProven) {
        sync(true); // a broadcast means somebody moved it deliberately
      } else {
        // No adapter, or one that never answered: follow by rebuilding.
        reloadSync();
      }
      refreshBars();
      refreshTimeline();
      status.replaceChildren(); // clear any hint before writing plain text
      status.textContent = statusLine();
      renderQueue(); // the skip vote exists only while something is on
    },

    refreshBars,
    pinInput,
  };
}

export default { createRoomVideo };
